import React, { createContext, useContext, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import config from '../config';
const ServiceContext = createContext();

export const ServiceProvider = ({ children }) => {
  const token = useSelector((state) => state.user.token);
  const [serviceData, setServiceData] = useState({
    used: 0,
    uses_left: 0,
    max_uses: 0,
    plan_name: null,
    loading: true,
    service_found: false
  });

  const fetchServiceStatus = async () => {
    // No token yet (logged out, or on a public page like Home/Login) —
    // nothing to fetch. Reset to a clean "not logged in" state instead of
    // hitting the API with Authorization: Bearer null and getting a 401.
    if (!token) {
      setServiceData({
        used: 0,
        uses_left: 0,
        max_uses: 0,
        plan_name: null,
        loading: false,
        service_found: false
      });
      return;
    }

    try {
      setServiceData(prev => ({ ...prev, loading: true }));

      const response = await fetch(`${config.baseURL}/api/profile/service/status?service_name=Shortlist`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // A 401 here just means "not subscribed" or a stale/expired token —
      // both are normal states for this endpoint, not failures worth
      // logging as errors.
      if (response.status === 401) {
        setServiceData(prev => ({ ...prev, loading: false, service_found: false }));
        return;
      }

      const data = await response.json();

      if (data.success) {
        const serviceInfo = data.services?.Shortlist || data.services;

        if (serviceInfo) {
          setServiceData({
            used: serviceInfo.used || 0,
            uses_left: serviceInfo.uses_left || 0,
            max_uses: serviceInfo.max_uses || 0,
            plan_name: data.plan_name,
            loading: false,
            service_found: true
          });
        } else {
          setServiceData(prev => ({ ...prev, loading: false, service_found: false }));
        }
      } else {
        setServiceData(prev => ({ ...prev, loading: false, service_found: false }));
      }
    } catch (error) {
      // Only genuine network/parse errors land here now — 401s are handled
      // above without throwing, so this stays quiet for the common case.
      console.error('Error fetching service status:', error);
      setServiceData(prev => ({ ...prev, loading: false }));
    }
  };

  const updateServiceUsage = (newUsed, newUsesLeft) => {
    setServiceData(prev => ({
      ...prev,
      used: newUsed,
      uses_left: newUsesLeft
    }));
  };

  // Re-fetch whenever the token changes — covers both "user just logged in"
  // (token goes from null/undefined to a real value) and "user logged out"
  // (goes back to null), instead of only ever running once on first mount.
  useEffect(() => {
    fetchServiceStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <ServiceContext.Provider value={{ serviceData, updateServiceUsage, refetchServiceStatus: fetchServiceStatus }}>
      {children}
    </ServiceContext.Provider>
  );
};

export const useService = () => {
  const context = useContext(ServiceContext);
  if (!context) {
    throw new Error('useService must be used within a ServiceProvider');
  }
  return context;
};